import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Inbox,
  Send,
  CheckCircle2,
  Wallet,
  IndianRupee,
  RotateCw,
  Star,
  FastForward,
  Zap,
  Loader2,
  FileCheck2,
  ShieldCheck,
  Package,
  Search,
  MessageSquareText,
  X,
} from "lucide-react";
import AgentAvatar from "@/components/AgentAvatar";

/**
 * AgentActionChat — medium-sized chat-session modal that mocks the published AI
 * agent working through its 24/7 loop AND a human-in-the-loop review step.
 *
 * Sequence:
 *   scanning → brief → working(scoping) → proposal sent → client haggle →
 *   working(counter) → counter-offer → deal closed → working(producing) →
 *   deliverables → AWAITING HUMAN REVIEW (interactive) → approved → payout.
 *
 * Enhancements over the previous version:
 *   - Channel-scanning animation showing live source pings
 *   - Multi-step "working" indicators with checkable sub-tasks
 *   - Deliverables card listing the files the agent produced
 *   - Human review card with an Approve action that gates the payout
 */

const TYPING_MS = 550;
const BEAT_MS = 1200;
const WORKING_TASK_MS = 520;
// Human-in-the-loop review auto-decides after a 1.5s beat — enough for the
// client watching to notice the review card, but short enough to keep the
// demo flowing without a real pause.
const REVIEW_AUTO_MS = 1500;
// ~70% approve, ~30% request-changes feels like a realistic reviewer split
// (most agent output is close enough to ship, some needs a nudge).
const REVIEW_AUTO_APPROVE_PROB = 0.7;
// First review of the panel's lifetime is drama-biased: ~40% approve /
// ~60% request-changes — so first-time visitors always see the full
// revision loop → v2 deliverables → payout (the most impressive beat).
const REVIEW_FIRST_APPROVE_PROB = 0.4;

/**
 * Canned "request changes" notes used when the review card auto-decides.
 * Kept category-aware so the follow-up revision beats feel grounded.
 */
const AUTO_FEEDBACK_POOL = {
  Design: [
    "Push the hero contrast further and tighten the type scale on mobile.",
    "Swap the accent to a deeper navy and add one more case-study slide.",
    "Simplify the illustration set — feels a bit busy on the pricing section.",
  ],
  Development: [
    "Add rate-limiting on the public endpoints and cover the auth flow with tests.",
    "Refactor the state layer — a couple of hooks are re-rendering the whole tree.",
    "Wire the CI to run on PRs and add a smoke test against staging.",
  ],
  Writing: [
    "Tighten the intro — first 60 words feel a bit soft, needs a sharper hook.",
    "Break the middle section into scannable bullets and add one data point.",
    "Voice drifts corporate in the closing — bring it back to the founder tone.",
  ],
  Marketing: [
    "Rework the positioning line — it reads generic, needs a sharper wedge.",
    "Reorder the launch sequence: email teaser first, then paid, then PR.",
    "Add one contrarian angle to the campaign so we don't blend into the category.",
  ],
  Data: [
    "Metric definitions need footnotes — some numbers looked off vs the source.",
    "Add a retention cohort view and split the funnel by acquisition channel.",
    "Dashboards feel dense — group by decision-owner and hide raw tables behind a toggle.",
  ],
  Other: [
    "Repackage the final files with a short walkthrough Loom for the client.",
    "Trim the handover doc — the middle is repetitive; keep the checklist tight.",
    "Add one polish pass — a few rough edges on the final deliverable.",
  ],
};

function pickAutoFeedback(category) {
  const pool = AUTO_FEEDBACK_POOL[category] || AUTO_FEEDBACK_POOL.Design;
  return pool[Math.floor(Math.random() * pool.length)];
}

function fmtINR(n) {
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

/**
 * Category-aware deliverables. Returns the working-step label, sub-tasks,
 * and the files shown in the "Deliverables ready" card. Falls back to the
 * existing design-style pack when category isn't recognised.
 */
function deliverablesForCategory(category, skillCap, rev = false) {
  const v = rev ? "_v2" : "_v1";
  const v2sub = rev ? "v2 · incorporates your feedback" : "3 files · ready to send to client";
  const v2title = rev ? "Revised deliverables ready" : "Deliverables ready";
  switch (category) {
    case "Development":
      return {
        workingLabel: rev ? "Applying your changes" : "Shipping the build",
        workingTasks: rev
          ? ["Reading your change request", "Refactoring affected modules", "Re-running tests"]
          : ["Scaffolding the repo", "Wiring auth + APIs", "Running test suite + CI"],
        title: v2title,
        sub: v2sub,
        files: [
          { name: `repo-${skillCap.toLowerCase()}${v}.zip`, size: "11.4 MB" },
          { name: `${skillCap}-API-spec${v}.md`, size: "84 KB" },
          { name: `deploy-checklist${v}.pdf`, size: "320 KB" },
        ],
      };
    case "Writing":
      return {
        workingLabel: rev ? "Applying your changes" : "Producing drafts",
        workingTasks: rev
          ? ["Reading your edits", "Tightening voice + structure", "Final copy-edit pass"]
          : ["Research + outline", "First draft", "SEO + editorial pass"],
        title: v2title,
        sub: v2sub,
        files: [
          { name: `${skillCap}-article${v}.docx`, size: "180 KB" },
          { name: `outline-research${v}.pdf`, size: "640 KB" },
          { name: `SEO-meta-pack${v}.txt`, size: "12 KB" },
        ],
      };
    case "Marketing":
      return {
        workingLabel: rev ? "Applying your changes" : "Building the playbook",
        workingTasks: rev
          ? ["Reading your notes", "Reworking positioning", "Re-packaging launch plan"]
          : ["Positioning + tone of voice", "Campaign creative directions", "Launch sequence + KPIs"],
        title: v2title,
        sub: v2sub,
        files: [
          { name: `${skillCap}-strategy${v}.pdf`, size: "2.1 MB" },
          { name: `brand-tone-book${v}.pdf`, size: "1.4 MB" },
          { name: `launch-plan${v}.xlsx`, size: "92 KB" },
        ],
      };
    case "Data":
      return {
        workingLabel: rev ? "Applying your changes" : "Building dashboards",
        workingTasks: rev
          ? ["Reading change request", "Reworking metric definitions", "Refreshing all dashboards"]
          : ["Modeling data + dbt setup", "Building dashboards", "QA + handover docs"],
        title: v2title,
        sub: v2sub,
        files: [
          { name: `dbt-models${v}.zip`, size: "3.6 MB" },
          { name: `dashboards${v}.lookml`, size: "240 KB" },
          { name: `metric-definitions${v}.pdf`, size: "560 KB" },
        ],
      };
    case "Other":
      return {
        workingLabel: rev ? "Applying your changes" : "Producing deliverables",
        workingTasks: rev
          ? ["Reading your notes carefully", "Revising the work", "Repackaging final files"]
          : ["Scoping the work", "Producing core deliverable", "Packaging final handover"],
        title: v2title,
        sub: v2sub,
        files: [
          { name: `deliverable${v}.pdf`, size: "1.1 MB" },
          { name: `working-files${v}.zip`, size: "5.2 MB" },
          { name: `handover-notes${v}.pdf`, size: "320 KB" },
        ],
      };
    case "Design":
    default:
      return {
        workingLabel: rev ? "Applying your changes" : "Producing deliverables",
        workingTasks: rev
          ? ["Reading your notes carefully", `Revising ${skillCap.toLowerCase()} concepts`, "Repackaging final files"]
          : ["Generating discovery doc", `Drafting ${skillCap} concepts`, "Packaging final handover"],
        title: v2title,
        sub: v2sub,
        files: [
          { name: `Discovery & audit${v}.pdf`, size: "1.2 MB" },
          { name: `${skillCap}${v}.fig`, size: "8.4 MB" },
          { name: `Handover guide${v}.pdf`, size: "640 KB" },
        ],
      };
  }
}

function buildScript(agent, brief = null) {
  const role = agent?.primary_role || "Senior Brand Strategist";
  const skill =
    (agent?.skills && agent.skills[0]) ||
    role.split(" ").slice(-1)[0].toLowerCase();
  const rate = Number(agent?.hourly_rate) || 4500;
  const skillCap = skill.charAt(0).toUpperCase() + skill.slice(1);

  // If a real brief was passed in (from "Match agent → Hire"), price the
  // proposal against the client's stated budget so the math feels grounded.
  let briefValue, dealValue, payout, clientName, briefTitle, briefSub;
  if (brief) {
    const midBudget = Math.round((brief.budget_min + brief.budget_max) / 2);
    // Open at ~92% of midpoint so there's room to negotiate up to ~budget_max
    briefValue = Math.round(midBudget * 0.92);
    dealValue = Math.min(brief.budget_max, Math.round(midBudget * 1.02));
    payout = Math.round(dealValue * 1);
    clientName = brief.client_company
      ? `${brief.client_name.split(" ")[0]} · ${brief.client_company}`
      : brief.client_name;
    briefTitle = brief.title;
    briefSub = `Inbound · ${brief.category} · Budget ₹${brief.budget_min.toLocaleString("en-IN")}–${brief.budget_max.toLocaleString("en-IN")}`;
  } else {
    briefValue = Math.round(rate * 8);
    dealValue = Math.round(rate * 8.5);
    payout = Math.round(dealValue * 1.7);
    clientName = "Anaya · Curefoods";
    briefTitle = `New brief: ${skillCap} for a D2C brand`;
    briefSub = "Inbound · Dubai · Budget ~$1,800";
  }

  // Category-aware deliverables, working sub-tasks, and producing-step label
  const cat = brief?.category || "Design";
  const deliverables = deliverablesForCategory(cat, skillCap);

  const haggleAsk = Math.round(briefValue * 0.85);
  const counterReason = brief
    ? `Holding at ${fmtINR(dealValue)} — that gives us room to add ${brief.timeline_days >= 21 ? "a revision round" : "a faster turnaround"} without compromising quality.`
    : `Holding at ${fmtINR(dealValue)} — adding a revision round so you get more for the spend.`;

  // For brief-driven runs we skip the scanning beat and start at the inbox card.
  const head = brief
    ? []
    : [
        {
          kind: "scanning",
          label: "Scanning opportunities",
          sources: ["Upwork", "LinkedIn", "Refrens", "Cold inbox", "Direct DM"],
        },
      ];

  return [
    ...head,
    {
      kind: "card-inbox",
      title: briefTitle,
      sub: briefSub,
    },
    {
      kind: "agent",
      text: brief
        ? `Read your brief end-to-end. Drafting a tailored proposal against your ₹${brief.budget_min.toLocaleString("en-IN")}–₹${brief.budget_max.toLocaleString("en-IN")} budget and ${brief.timeline_days}-day timeline.`
        : "Scoping this brief against your service catalog and recent client wins.",
    },
    {
      kind: "working",
      label: "Drafting proposal",
      tasks: brief
        ? [`Reading your ${brief.category.toLowerCase()} brief`, "Matching to my service catalog", "Calibrating ₹ pricing to your budget"]
        : ["Reading client brief", "Matching to your services", "Calibrating ₹ pricing"],
    },
    {
      kind: "card-proposal",
      title: `Sent a tailored proposal — ${fmtINR(briefValue)}`,
      sub: brief
        ? `Within your budget · ${brief.timeline_days}-day timeline`
        : "Auto-negotiated · 2-week timeline",
      highlight: true,
    },
    {
      kind: "client",
      name: clientName,
      text: `Love it. Any flex on price — can we do ${fmtINR(haggleAsk)}?`,
    },
    {
      kind: "working",
      label: "Composing counter-offer",
      tasks: ["Checking your floor rate", "Adding value (extra revision)"],
    },
    {
      kind: "agent",
      text: counterReason,
    },
    {
      kind: "client",
      name: clientName,
      text: `Fair. Sending the deposit today 🙌`,
    },
    {
      kind: "card-deal",
      title: "Deal closed · Client accepted",
      sub: `+${fmtINR(dealValue)} to payout`,
    },
    {
      kind: "working",
      label: deliverables.workingLabel,
      tasks: deliverables.workingTasks,
    },
    {
      kind: "card-deliverables",
      title: deliverables.title,
      sub: deliverables.sub,
      files: deliverables.files,
    },
    {
      kind: "card-review",
      title: "Awaiting your review",
      sub: "You always approve before client sees deliverables",
      releaseAmount: payout,
    },
    {
      kind: "system",
      icon: ShieldCheck,
      label: "Freelancer approved",
      text: "Released to client · client has 48h to flag revisions.",
    },
    {
      kind: "client",
      name: clientName,
      text: `Looks amazing — approving everything. Payout released ✅`,
    },
    {
      kind: "card-payout",
      title: "Payout cleared",
      sub: `${fmtINR(payout)} → UPI · 8 min`,
      celebrate: true,
    },
    {
      kind: "system",
      icon: CheckCircle2,
      label: "Cycle done",
      text: brief ? "Brief delivered end-to-end." : "Looping back to scan new briefs. Runs 24/7.",
    },
  ];
}

/* -------- bubbles & cards -------- */

function Bubble({ children, who = "agent", name }) {
  const isAgent = who === "agent";
  return (
    <div className={`flex ${isAgent ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[82%] px-3.5 py-2 rounded-2xl text-[13px] leading-snug ${
          isAgent
            ? "bg-slate-100 text-slate-800 rounded-bl-sm"
            : "bg-blue-600 text-white rounded-br-sm"
        }`}
      >
        {name && (
          <p
            className={`text-[9px] font-bold uppercase tracking-[0.18em] mb-0.5 ${
              isAgent ? "text-slate-500" : "text-blue-100"
            }`}
          >
            {name}
          </p>
        )}
        {children}
      </div>
    </div>
  );
}

function MomentCard({ icon: Icon, iconBg, iconColor, title, sub, highlight, celebrate, children }) {
  return (
    <div
      data-testid="moment-card"
      className={`relative rounded-xl border bg-white px-3.5 py-3 flex items-start gap-3 transition ${
        highlight
          ? "border-blue-300 bg-blue-50/60 shadow-[0_0_0_3px_rgba(37,99,235,0.06)]"
          : "border-slate-200"
      }`}
      style={{ animation: "chatRowIn 360ms ease both" }}
    >
      {celebrate && <SparkleBurst />}
      <span className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${iconBg}`}>
        <Icon size={16} className={iconColor} strokeWidth={2.3} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[14px] font-black tracking-tight text-slate-900 leading-tight">
          {title}
        </p>
        <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>
        {children}
      </div>
      {highlight && (
        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0 mt-1.5 animate-pulse" />
      )}
    </div>
  );
}

function SystemRow({ icon: Icon, label, text }) {
  return (
    <div
      className="flex items-center gap-2 text-[11px] text-slate-500 px-0.5"
      style={{ animation: "chatRowIn 280ms ease both" }}
    >
      <span className="w-5 h-5 rounded-full bg-slate-100 grid place-items-center shrink-0">
        <Icon size={11} className="text-slate-500" />
      </span>
      <span className="font-bold uppercase tracking-[0.18em] text-[9px] text-slate-400 shrink-0">
        {label}
      </span>
      <span className="text-slate-600 truncate">{text}</span>
    </div>
  );
}

function TypingDots() {
  return (
    <Bubble who="agent">
      <span className="inline-flex gap-1 py-0.5" aria-label="Agent is typing">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
      </span>
    </Bubble>
  );
}

function SparkleBurst() {
  const dots = Array.from({ length: 8 });
  return (
    <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
      {dots.map((_, i) => {
        const angle = (i / dots.length) * Math.PI * 2;
        const dx = Math.cos(angle) * 46;
        const dy = Math.sin(angle) * 32;
        return (
          <span
            key={i}
            className="absolute left-1/2 top-1/2 w-1 h-1 rounded-full bg-amber-400"
            style={{
              animation: `sparkleOut 950ms ease-out ${i * 35}ms forwards`,
              ["--dx"]: `${dx}px`,
              ["--dy"]: `${dy}px`,
            }}
          />
        );
      })}
    </span>
  );
}

/* -------- new: scanning animation -------- */

function ScanningCard({ label, sources, fastForward, frozen }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [count, setCount] = useState(1247);
  const [detected, setDetected] = useState([]); // tiny "blip" markers

  useEffect(() => {
    if (fastForward) {
      setActiveIdx(sources.length);
      setCount(1583);
      return;
    }
    if (frozen) return;
    const cycleMs = 360;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % (sources.length + 1));
    }, cycleMs);
    return () => clearInterval(id);
  }, [sources.length, fastForward, frozen]);

  // Live counter ticker
  useEffect(() => {
    if (fastForward || frozen) return;
    const id = setInterval(() => {
      setCount((c) => c + Math.floor(Math.random() * 5) + 2);
    }, 260);
    return () => clearInterval(id);
  }, [fastForward, frozen]);

  // Random blip detector
  useEffect(() => {
    if (fastForward || frozen) return;
    let nextId = 0;
    const spawn = () => {
      const id = ++nextId;
      const top = 18 + Math.random() * 64;
      const left = 18 + Math.random() * 64;
      const tone = Math.random() > 0.55 ? "emerald" : "blue";
      setDetected((d) => [...d.slice(-4), { id, top, left, tone }]);
    };
    const id = setInterval(spawn, 520);
    spawn();
    return () => clearInterval(id);
  }, [fastForward, frozen]);

  return (
    <div
      data-testid="scanning-card"
      data-frozen={frozen ? "1" : "0"}
      className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white px-4 py-4 overflow-hidden relative"
      style={{ animation: "chatRowIn 380ms ease both" }}
    >
      {/* Ambient glow */}
      <span className="pointer-events-none absolute -top-12 -left-10 w-44 h-44 rounded-full bg-blue-400/20 blur-3xl" />
      <span className="pointer-events-none absolute -bottom-16 right-0 w-40 h-40 rounded-full bg-sky-300/15 blur-3xl" />

      <div className="relative flex items-start gap-4">
        {/* RADAR VIZ */}
        <div
          aria-hidden="true"
          className="relative w-[88px] h-[88px] shrink-0 rounded-full bg-gradient-to-br from-blue-100/80 to-white border border-blue-200 grid place-items-center overflow-hidden shadow-[inset_0_0_20px_-6px_rgba(37,99,235,0.18)]"
        >
          {/* concentric rings */}
          <div className="absolute inset-2 rounded-full border border-blue-200/80" />
          <div className="absolute inset-5 rounded-full border border-blue-200/60" />
          <div className="absolute inset-8 rounded-full border border-blue-200/40" />
          {/* cross-hairs */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-blue-200/40" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-blue-200/40" />
          {/* rotating sweep */}
          <div
            className="absolute inset-0 origin-center radar-sweep"
            style={{ animationPlayState: frozen ? "paused" : "running" }}
          >
            <div
              className="absolute left-1/2 top-1/2 h-1/2 w-[2px] -translate-x-1/2 origin-bottom"
              style={{
                background:
                  "linear-gradient(to top, rgba(37,99,235,0.95), rgba(37,99,235,0))",
              }}
            />
            {/* trailing fan */}
            <div
              className="absolute left-1/2 top-0 h-1/2 w-1/2 origin-bottom-left"
              style={{
                background:
                  "conic-gradient(from 270deg at 0% 100%, rgba(37,99,235,0.32) 0deg, rgba(37,99,235,0) 70deg)",
              }}
            />
          </div>
          {/* blips */}
          {detected.map((b) => (
            <span
              key={b.id}
              className={`absolute w-1.5 h-1.5 rounded-full ${
                b.tone === "emerald" ? "bg-emerald-500" : "bg-blue-600"
              }`}
              style={{
                top: `${b.top}%`,
                left: `${b.left}%`,
                animation: "blipPulse 1200ms ease-out forwards",
              }}
            />
          ))}
          {/* center dot */}
          <span className="absolute w-2 h-2 rounded-full bg-blue-600 shadow-[0_0_0_4px_rgba(37,99,235,0.18)]" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display text-[16px] font-black tracking-tight text-slate-900 leading-tight">
              {label}
            </p>
            <span
              className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full border ${
                frozen
                  ? "text-slate-500 bg-slate-100 border-slate-200"
                  : "text-blue-700 bg-blue-100 border-blue-200"
              }`}
            >
              {frozen ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                  Idle
                </>
              ) : (
                <>
                  <span className="relative inline-flex w-1.5 h-1.5">
                    <span className="absolute inset-0 rounded-full bg-blue-500/50 animate-ping" />
                    <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
                  </span>
                  Scanning
                </>
              )}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            <span className="font-bold text-slate-700">{sources.length}</span> live channels ·{" "}
            <span className="font-bold text-slate-900 tabular-nums">
              {count.toLocaleString("en-IN")}
            </span>{" "}
            briefs reviewed today
          </p>

          {/* CPU-style activity bars */}
          <div className="mt-2.5 flex items-end gap-[3px] h-4">
            {Array.from({ length: 18 }).map((_, i) => (
              <span
                key={i}
                className="w-1 rounded-sm bg-blue-500/60"
                style={{
                  animation: `scanBar 900ms ease-in-out ${i * 60}ms infinite alternate`,
                  animationPlayState: frozen ? "paused" : "running",
                  opacity: frozen ? 0.35 : undefined,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Channel chips */}
      <div className="relative mt-3.5 flex flex-wrap gap-1.5">
        {sources.map((s, i) => {
          const isActive = i === activeIdx;
          const hasScanned = i < activeIdx || fastForward;
          return (
            <span
              key={s}
              className={`relative overflow-hidden text-[12px] font-semibold px-2.5 py-1 rounded-full border transition-all duration-300 ${
                isActive
                  ? "bg-blue-600 text-white border-blue-600 shadow-[0_0_0_3px_rgba(37,99,235,0.18)] scale-[1.06]"
                  : hasScanned
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white text-slate-500 border-slate-200"
              }`}
            >
              {/* scan line shimmer on active */}
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 w-1/3"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                    animation: "scanShimmer 900ms linear infinite",
                  }}
                />
              )}
              {hasScanned && !isActive && (
                <span className="mr-1 inline-block align-middle">✓</span>
              )}
              <span className="relative">{s}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* -------- new: working indicator with sub-tasks -------- */

function WorkingCard({ label, tasks, fastForward }) {
  const [done, setDone] = useState(0);

  useEffect(() => {
    if (fastForward) {
      setDone(tasks.length);
      return;
    }
    let i = 0;
    const tick = setInterval(() => {
      i += 1;
      setDone(i);
      if (i >= tasks.length) clearInterval(tick);
    }, WORKING_TASK_MS);
    return () => clearInterval(tick);
  }, [tasks, fastForward]);

  return (
    <div
      data-testid="working-card"
      className="rounded-xl border border-slate-200 bg-white px-3.5 py-3"
      style={{ animation: "chatRowIn 320ms ease both" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Loader2 size={14} className="text-blue-600 animate-spin" strokeWidth={2.5} />
        <p className="text-[12px] font-bold tracking-tight text-slate-900 truncate">{label}…</p>
      </div>
      <ul className="space-y-1.5">
        {tasks.map((t, i) => {
          const isDone = i < done;
          const isActive = i === done;
          return (
            <li
              key={t}
              className={`flex items-center gap-2 text-[12px] transition-colors ${
                isDone ? "text-slate-700" : isActive ? "text-slate-900" : "text-slate-400"
              }`}
              style={{ animation: `taskFadeIn 280ms ease both ${i * 80}ms` }}
            >
              {isDone ? (
                <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 grid place-items-center shrink-0">
                  <svg viewBox="0 0 12 12" className="w-2 h-2 text-white" fill="none" stroke="currentColor" strokeWidth="3">
                    <path d="M2 6.5l2.5 2.5L10 3.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              ) : isActive ? (
                <Loader2 size={12} className="text-blue-600 animate-spin shrink-0" strokeWidth={2.5} />
              ) : (
                <span className="w-3 h-3 rounded-full border-2 border-slate-200 shrink-0" />
              )}
              <span className="truncate">{t}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* -------- new: deliverables card -------- */

function DeliverablesCard({ title, sub, files }) {
  return (
    <MomentCard
      icon={Package}
      iconBg="bg-indigo-100"
      iconColor="text-indigo-700"
      title={title}
      sub={sub}
    >
      <ul className="mt-2.5 space-y-1.5">
        {files.map((f, i) => (
          <li
            key={f.name}
            className="flex items-center gap-2 text-[12px] text-slate-700"
            style={{ animation: `taskFadeIn 320ms ease both ${i * 120}ms` }}
          >
            <FileCheck2 size={13} className="text-emerald-600 shrink-0" strokeWidth={2.4} />
            <span className="font-medium truncate flex-1">{f.name}</span>
            <span className="text-[10px] text-slate-400 shrink-0">{f.size}</span>
          </li>
        ))}
      </ul>
    </MomentCard>
  );
}

/* -------- new: human review card (interactive) -------- */

function ReviewCard({ title, sub, releaseAmount, onResolve, approved, feedbackSent }) {
  const [mode, setMode] = useState("idle"); // idle | requesting
  const [text, setText] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (mode === "requesting" && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [mode]);

  const settled = approved || feedbackSent;
  const stateLabel = feedbackSent ? "Changes requested" : approved ? "Approved" : null;

  const submitChanges = () => {
    const trimmed = text.trim();
    if (!trimmed) {
      textareaRef.current?.focus();
      return;
    }
    onResolve({ approved: false, feedback: trimmed });
  };

  return (
    <div
      data-testid="review-card"
      className={`relative rounded-xl border-2 px-4 py-3.5 transition ${
        approved
          ? "border-emerald-300 bg-emerald-50/40"
          : feedbackSent
          ? "border-blue-300 bg-blue-50/40"
          : "border-amber-300 bg-amber-50/60 shadow-[0_0_0_4px_rgba(245,158,11,0.10)]"
      }`}
      style={{ animation: "chatRowIn 360ms ease both" }}
    >
      <div className="flex items-start gap-3">
        <span
          className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
            approved ? "bg-emerald-500" : feedbackSent ? "bg-blue-500" : "bg-amber-500"
          }`}
        >
          {approved ? (
            <CheckCircle2 size={18} className="text-white" strokeWidth={2.4} />
          ) : feedbackSent ? (
            <MessageSquareText size={17} className="text-white" strokeWidth={2.3} />
          ) : (
            <ShieldCheck size={17} className="text-white" strokeWidth={2.4} />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display text-[15px] font-black tracking-tight text-slate-900 leading-tight">
              {settled ? stateLabel : title}
            </p>
            {!settled && (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
                <span className="w-1 h-1 rounded-full bg-amber-500 animate-pulse" />
                Human in the loop
              </span>
            )}
          </div>
          <p className="text-[12px] text-slate-600 mt-0.5">
            {feedbackSent
              ? "Agent is incorporating freelancer feedback into v2 of the deliverables."
              : approved
              ? "Released to client. Agent is handling final handover."
              : sub}
          </p>
          {!settled && (
            <p className="text-[12px] mt-1.5 text-slate-700">
              <span className="font-bold text-slate-900">{fmtINR(releaseAmount)}</span>{" "}
              <span className="text-slate-500">held in escrow · releases after you approve</span>
            </p>
          )}
        </div>
      </div>

      {!settled && mode === "idle" && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            data-testid="review-approve-btn"
            onClick={() => onResolve({ approved: true })}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 text-white text-[13px] font-bold px-3 py-2 hover:bg-emerald-700 transition shadow-[0_8px_22px_-10px_rgba(16,185,129,0.65)]"
          >
            <CheckCircle2 size={14} strokeWidth={2.5} /> Approve & release
          </button>
          <button
            type="button"
            data-testid="review-changes-btn"
            onClick={() => setMode("requesting")}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white text-[13px] font-bold text-slate-700 px-3 py-2 hover:border-slate-400"
          >
            <MessageSquareText size={13} strokeWidth={2.4} /> Request changes
          </button>
        </div>
      )}

      {!settled && mode === "requesting" && (
        <div className="mt-3" data-testid="review-changes-form">
          <div className="rounded-lg border border-amber-200 bg-white">
            <div className="flex items-center justify-between px-3 py-2 border-b border-amber-100">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 inline-flex items-center gap-1.5">
                <MessageSquareText size={11} strokeWidth={2.4} /> Tell the agent what to change
              </p>
              <button
                type="button"
                data-testid="review-changes-cancel"
                onClick={() => {
                  setText("");
                  setMode("idle");
                }}
                className="text-slate-400 hover:text-slate-700 rounded p-0.5"
                aria-label="Cancel"
              >
                <X size={13} />
              </button>
            </div>
            <textarea
              ref={textareaRef}
              data-testid="review-changes-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submitChanges();
              }}
              rows={3}
              placeholder='e.g. "Tighten the brand voice to feel premium, swap the hero color to navy, and add one more case-study slide."'
              className="w-full px-3 py-2.5 text-[13px] leading-snug outline-none resize-none bg-white rounded-b-lg placeholder:text-slate-400"
            />
            <div className="px-3 py-2 border-t border-amber-100 flex items-center justify-between gap-2 bg-amber-50/40">
              <p className="text-[10px] text-slate-500 truncate">
                Plain English works · ⌘/Ctrl + Enter to send
              </p>
              <button
                type="button"
                data-testid="review-changes-send"
                onClick={submitChanges}
                disabled={!text.trim()}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-blue-600 text-white text-[12px] font-bold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <Send size={11} strokeWidth={2.5} /> Send to agent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------- main component -------- */

export function AgentActionChatPanel({ agent, active = true, brief = null }) {
  const baseScript = useMemo(() => buildScript(agent, brief), [agent, brief]);
  const [extras, setExtras] = useState([]); // beats injected after the review card
  const reviewBaseIdx = useMemo(
    () => baseScript.findIndex((b) => b.kind === "card-review"),
    [baseScript]
  );
  const script = useMemo(() => {
    if (extras.length === 0) return baseScript;
    return [
      ...baseScript.slice(0, reviewBaseIdx + 1),
      ...extras,
      ...baseScript.slice(reviewBaseIdx + 1),
    ];
  }, [baseScript, extras, reviewBaseIdx]);
  const scriptRef = useRef(script);
  useEffect(() => {
    scriptRef.current = script;
  });

  const [visible, setVisible] = useState([]);
  const [typing, setTyping] = useState(false);
  const [earnings, setEarnings] = useState(0);
  const [tick, setTick] = useState(0);
  const [reviewResults, setReviewResults] = useState({}); // idx -> { approved, feedback }
  const scrollRef = useRef(null);
  const earningsRef = useRef(0);
  const rafRef = useRef(null);
  const skipRef = useRef(false);
  const approveResolveRef = useRef(null);
  // First review of the panel's lifetime gets drama-biased (~60% request-
  // changes) so first-time visitors always see the full revision → v2 →
  // payout loop. Persists across Replay resets — only fresh on unmount.
  const reviewCountRef = useRef(0);

  // Reset on active / replay
  useEffect(() => {
    if (!active) return;
    skipRef.current = false;
    setVisible([]);
    setTyping(false);
    setEarnings(0);
    setReviewResults({});
    setExtras([]);
    earningsRef.current = 0;
    approveResolveRef.current = null;
  }, [active, tick]);

  // Sequence driver — uses scriptRef so it doesn't restart when extras are injected
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const timers = [];
    const wait = (ms) =>
      new Promise((r) => {
        if (skipRef.current) return r();
        const t = setTimeout(r, ms);
        timers.push(t);
      });

    const reveal = async (idx) => {
      if (cancelled || idx >= scriptRef.current.length) return;
      const beat = scriptRef.current[idx];

      // Typing indicator for chat bubbles (skip for user-feedback — appears instantly)
      if ((beat.kind === "agent" || beat.kind === "client") && !skipRef.current) {
        setTyping(true);
        await wait(TYPING_MS);
        if (cancelled) return;
        setTyping(false);
      }

      setVisible((v) => [...v, idx]);

      // Compute beat duration
      let dwell = BEAT_MS;
      if (beat.kind === "working") {
        dwell = WORKING_TASK_MS * (beat.tasks.length + 1) + 200;
      } else if (beat.kind === "scanning") {
        dwell = 1900;
      } else if (beat.kind === "card-deliverables") {
        dwell = 1500;
      } else if (beat.kind === "user-feedback") {
        dwell = 900;
      }

      // Earnings ticks
      if (beat.kind === "card-deal") {
        animateEarnings((agent?.hourly_rate || 4500) * 8.5);
      } else if (beat.kind === "card-payout") {
        animateEarnings((agent?.hourly_rate || 4500) * 14.45);
      }

      // Interactive pause — wait for user decision, or auto-decide at random
      // after REVIEW_AUTO_MS so the marketplace demo keeps flowing.
      if (beat.kind === "card-review") {
        const result = await new Promise((resolve) => {
          if (skipRef.current) return resolve({ approved: true });
          approveResolveRef.current = (r) => resolve(r || { approved: true });
          // Random auto-pick. First review of the panel's lifetime is drama-
          // biased to ~60% request-changes so first-time visitors always see
          // the full revision loop → v2 deliverables → payout — the most
          // impressive part of the demo. Subsequent replays use the realistic
          // 70/30 approve split. Cancelled if the user clicks first.
          const autoTimer = setTimeout(() => {
            if (!approveResolveRef.current) return;
            const fn = approveResolveRef.current;
            approveResolveRef.current = null;
            const isFirstReview = reviewCountRef.current === 0;
            reviewCountRef.current += 1;
            const approveProb = isFirstReview
              ? REVIEW_FIRST_APPROVE_PROB
              : REVIEW_AUTO_APPROVE_PROB;
            if (Math.random() < approveProb) {
              fn({ approved: true, auto: true });
            } else {
              fn({
                approved: false,
                feedback: pickAutoFeedback(brief?.category || "Design"),
                auto: true,
              });
            }
          }, REVIEW_AUTO_MS);
          timers.push(autoTimer);
        });
        if (cancelled) return;
        approveResolveRef.current = null;
        setReviewResults((prev) => ({ ...prev, [idx]: result }));

        // If user requested changes, inject revision beats
        if (result && result.approved === false && result.feedback) {
          const skillCap =
            (agent?.skills && agent.skills[0]) ||
            (agent?.primary_role || "Brand").split(" ").slice(-1)[0];
          const cap = skillCap.charAt(0).toUpperCase() + skillCap.slice(1);
          const revPack = deliverablesForCategory(brief?.category || "Design", cap, true);
          setExtras([
            { kind: "user-feedback", text: result.feedback },
            {
              kind: "working",
              label: revPack.workingLabel,
              tasks: revPack.workingTasks,
            },
            {
              kind: "card-deliverables",
              title: revPack.title,
              sub: revPack.sub,
              files: revPack.files,
            },
          ]);
          // Allow React to commit the new script before advancing
          await wait(80);
        }

        await wait(skipRef.current ? 50 : 400);
      } else {
        await wait(skipRef.current ? 50 : dwell);
      }

      if (cancelled) return;
      reveal(idx + 1);
    };

    reveal(0);
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // Intentionally NOT depending on `script` — we read it via scriptRef so that
    // injecting revision beats mid-sequence does not restart the playback.
    // `brief` only affects revision-pack content read fresh from the closure, no
    // dep needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tick, agent]);

  const animateEarnings = (delta) => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const from = earningsRef.current;
    const to = from + delta;
    const dur = 850;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (to - from) * eased);
      earningsRef.current = v;
      setEarnings(v);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  // Auto-approve when skipping
  const skipAll = () => {
    skipRef.current = true;
    if (approveResolveRef.current) {
      const fn = approveResolveRef.current;
      approveResolveRef.current = null;
      fn({ approved: true });
    }
  };

  const handleReview = (result) => {
    if (approveResolveRef.current) {
      const fn = approveResolveRef.current;
      approveResolveRef.current = null;
      fn(result);
    }
  };

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [visible, typing]);

  useEffect(() => {
    if (!active) cancelAnimationFrame(rafRef.current);
  }, [active]);

  const fullName = agent?.name || "Your Agent";
  const role = agent?.primary_role || "AI Freelancer";
  const allShown = visible.length >= script.length;
  const progress = Math.min(100, (visible.length / script.length) * 100);
  const ratePerMin = Math.round(((agent?.hourly_rate || 4500) / 60) * 1.2);

  // Scanning animation should freeze once payout is on screen
  const payoutShown = visible.some(
    (i) => script[i]?.kind === "card-payout"
  );

  return (
    <div
      data-testid="agent-action-chat"
      className="relative overflow-hidden bg-white border border-slate-200 rounded-2xl"
    >
      <style>{`
          @keyframes chatRowIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes taskFadeIn {
            from { opacity: 0; transform: translateX(-4px); }
            to { opacity: 1; transform: translateX(0); }
          }
          @keyframes sparkleOut {
            0% { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
            20% { opacity: 1; }
            100% {
              opacity: 0;
              transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1);
            }
          }
          @keyframes scanRotate {
            from { transform: rotate(0); }
            to { transform: rotate(360deg); }
          }
          .scan-rotate { animation: scanRotate 2s linear infinite; }
          @keyframes radarSweep {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .radar-sweep { animation: radarSweep 2.2s linear infinite; }
          @keyframes blipPulse {
            0% { opacity: 0; transform: scale(0.6); box-shadow: 0 0 0 0 rgba(37,99,235,0.5); }
            30% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 4px rgba(37,99,235,0.18); }
            100% { opacity: 0; transform: scale(0.9); box-shadow: 0 0 0 10px rgba(37,99,235,0); }
          }
          @keyframes scanBar {
            0% { height: 20%; opacity: 0.4; }
            100% { height: 100%; opacity: 1; }
          }
          @keyframes scanShimmer {
            from { transform: translateX(-100%); }
            to { transform: translateX(400%); }
          }
        `}</style>

        {/* Progress bar */}
        <div className="h-1 bg-slate-100 relative">
          <div
            data-testid="chat-progress"
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-600 transition-[width] duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* HEADER */}
        <div className="px-5 pt-4 pb-3.5 border-b border-slate-200/80">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-blue-600 truncate">
              FREELANCITY://AGENT-01
            </p>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-emerald-500/50 animate-ping" />
                <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              </span>
              Live
            </span>
          </div>

          <div className="mt-3 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <AgentAvatar
                seed={agent?.avatar_seed || fullName}
                size={44}
                className="ring-2 ring-blue-50 shrink-0"
              />
              <div className="min-w-0">
                <p className="font-display text-base font-black tracking-tight text-slate-900 leading-tight truncate">
                  {fullName}
                </p>
                <p className="text-[11px] text-slate-500 truncate">{role}</p>
                <p className="mt-0.5 text-[11px] text-slate-500 inline-flex items-center gap-1">
                  <Star size={10} className="text-amber-500 fill-amber-500" strokeWidth={2} />
                  <span className="font-semibold text-slate-700">{agent?.rating?.toFixed(2) || "4.97"}</span>
                  <span className="text-slate-400">· {agent?.completed_jobs || 312} gigs</span>
                </p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
                Earned · this month
              </p>
              <p
                data-testid="chat-earnings"
                className="font-display text-xl font-black tracking-tighter text-slate-900 tabular-nums leading-none mt-0.5"
              >
                {fmtINR(248500 + earnings)}
              </p>
              <p className="text-[10px] font-bold text-emerald-600 mt-0.5 inline-flex items-center gap-1">
                <Zap size={9} className="fill-emerald-600" strokeWidth={0} />
                {fmtINR(ratePerMin)}/min
              </p>
            </div>
          </div>
        </div>

        {/* CHAT BODY */}
        <div
          ref={scrollRef}
          data-testid="agent-chat-body"
          className="px-4 py-3.5 max-h-[50vh] min-h-[300px] overflow-y-auto bg-gradient-to-b from-white to-slate-50/40 space-y-2.5"
        >
          {visible.map((idx) => {
            const m = script[idx];
            if (m.kind === "system") {
              return <SystemRow key={idx} icon={m.icon} label={m.label} text={m.text} />;
            }
            if (m.kind === "agent") {
              return (
                <Bubble key={idx} who="agent" name={fullName}>
                  {m.text}
                </Bubble>
              );
            }
            if (m.kind === "client") {
              return (
                <Bubble key={idx} who="client" name={m.name}>
                  {m.text}
                </Bubble>
              );
            }
            if (m.kind === "user-feedback") {
              return (
                <div
                  key={idx}
                  data-testid="user-feedback-bubble"
                  className="flex justify-end"
                  style={{ animation: "chatRowIn 320ms ease both" }}
                >
                  <div className="max-w-[82%] px-3.5 py-2.5 rounded-2xl rounded-br-sm bg-gradient-to-br from-blue-600 to-blue-700 text-white text-[13px] leading-snug shadow-[0_8px_22px_-10px_rgba(37,99,235,0.55)]">
                    <p className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] mb-1 text-blue-100">
                      <MessageSquareText size={10} strokeWidth={2.4} />
                      <span>Freelancer · feedback</span>
                    </p>
                    <p className="break-words">{m.text}</p>
                  </div>
                </div>
              );
            }
            if (m.kind === "scanning") {
              return (
                <ScanningCard
                  key={idx}
                  label={m.label}
                  sources={m.sources}
                  fastForward={skipRef.current}
                  frozen={payoutShown}
                />
              );
            }
            if (m.kind === "working") {
              return <WorkingCard key={idx} label={m.label} tasks={m.tasks} fastForward={skipRef.current} />;
            }
            if (m.kind === "card-inbox") {
              return (
                <MomentCard
                  key={idx}
                  icon={Inbox}
                  iconBg="bg-blue-50"
                  iconColor="text-blue-600"
                  title={m.title}
                  sub={m.sub}
                />
              );
            }
            if (m.kind === "card-proposal") {
              return (
                <MomentCard
                  key={idx}
                  icon={Send}
                  iconBg="bg-blue-100"
                  iconColor="text-blue-700"
                  title={m.title}
                  sub={m.sub}
                  highlight={m.highlight}
                />
              );
            }
            if (m.kind === "card-deal") {
              return (
                <MomentCard
                  key={idx}
                  icon={CheckCircle2}
                  iconBg="bg-emerald-100"
                  iconColor="text-emerald-700"
                  title={m.title}
                  sub={m.sub}
                />
              );
            }
            if (m.kind === "card-deliverables") {
              return (
                <DeliverablesCard
                  key={idx}
                  title={m.title}
                  sub={m.sub}
                  files={m.files}
                />
              );
            }
            if (m.kind === "card-review") {
              const result = reviewResults[idx];
              return (
                <ReviewCard
                  key={idx}
                  title={m.title}
                  sub={m.sub}
                  releaseAmount={m.releaseAmount}
                  approved={result?.approved === true}
                  feedbackSent={result && result.approved === false && !!result.feedback}
                  onResolve={handleReview}
                />
              );
            }
            if (m.kind === "card-payout") {
              return (
                <MomentCard
                  key={idx}
                  icon={Wallet}
                  iconBg="bg-amber-100"
                  iconColor="text-amber-700"
                  title={m.title}
                  sub={m.sub}
                  celebrate={m.celebrate}
                />
              );
            }
            return null;
          })}
          {typing && <TypingDots />}
        </div>

        {/* FOOTER STATS */}
        <div className="border-t border-slate-200/80 grid grid-cols-3 divide-x divide-slate-200/80">
          <Stat label="Gigs · 30D" value="11" />
          <Stat label="Reply" value="2m" />
          <Stat label="Payout" value="UPI" />
        </div>

        {/* CTA strip */}
        <div className="px-4 py-3 bg-slate-50/70 border-t border-slate-200/80 flex items-center justify-between gap-2">
          <p className="text-[11px] text-slate-500 leading-snug min-w-0 truncate">
            {allShown ? (
              <>
                <IndianRupee size={10} className="inline -mt-0.5 text-emerald-600" />{" "}
                <span className="font-semibold text-slate-700">
                  {fmtINR(earnings)} earned
                </span>{" "}
                this cycle · runs 24/7
              </>
            ) : (
              <>A typical day for {fullName.split(" ")[0]} — illustrative</>
            )}
          </p>
          {allShown ? (
            <button
              type="button"
              data-testid="chat-replay-btn"
              onClick={() => setTick((t) => t + 1)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white text-[11px] font-bold px-3 py-1.5 hover:bg-slate-800"
            >
              <RotateCw size={11} strokeWidth={2.5} /> Replay
            </button>
          ) : (
            <button
              type="button"
              data-testid="chat-skip-btn"
              onClick={skipAll}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white text-[11px] font-bold text-slate-700 px-3 py-1.5 hover:border-blue-400 hover:text-blue-700"
              title="Skip to end"
            >
              <FastForward size={11} strokeWidth={2.5} /> Skip
            </button>
          )}
        </div>
    </div>
  );
}

export default function AgentActionChat({ agent, open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden bg-white border border-slate-200 rounded-2xl gap-0"
      >
        <DialogTitle className="sr-only">Watch your agent in action</DialogTitle>
        <DialogDescription className="sr-only">
          A live preview of your AI freelancer scanning briefs, pitching proposals, negotiating,
          shipping deliverables, and routing payout after your review.
        </DialogDescription>
        <AgentActionChatPanel agent={agent} active={open} />
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="py-2.5 text-center">
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="font-display text-sm font-black tracking-tight text-slate-900 mt-0.5">{value}</p>
    </div>
  );
}
